// Expedientes API Service
// Basado en documentación: expedientes_super_mega_prompt_modulo_completo.md

import { api } from './api';
import { withRetry } from './apiRetry';
import { handleApiError } from '@/utils/errorHandler';
import type {
  ExpedienteRead,
  ExpedienteReadWithFiles,
  ExpedienteCreate,
  ExpedienteUpdate,
  ExpedienteListResponse,
  ExpedienteSearchResponse,
  FormularioSeleccionResponse,
  ProponerTramiteResponse,
  ChecklistResponse,
  DocumentoRequisito,
  CompletitudResponse,
  ExpedienteHistorialResponse,
  ExpedienteEstadisticas,
  ExpedienteFilters,
  PlantillasFormulariosResponse,
  RequisitosVerificacionResponse,
} from '@/types/expediente';
import type {
  ExpedienteTiposResponse,
  ExpedienteTiposDetalladoResponse,
  ExpedienteTipoDetallado,
} from '@/types/expedienteTipos';
import type {
  TramiteMaestroResponse,
  GetTramiteMaestroParams,
} from '@/types/expedienteMaestro';

const EXPEDIENTES_BASE_PATH = '/expedientes';

// ── Análisis OCR + match (cards 161/162 backend) ──────────────────────────
export type CoverageEstado = 'cubierto' | 'falta' | 'conflicto' | 'opcional_vacio';

export interface CoverageItem {
  campo: string;
  /** Sección del formulario EX-32 (pili card 198): solicitante (OCR) o bloques del despacho. */
  bloque?: string;
  estado: CoverageEstado;
  valor: string | null;
  fuente_doc: string | null;
  confianza: number | null;
  fuentes_esperadas: string[];
  obligatorio: boolean;
  valores_doc?: { doc: string; valor: string }[];
}

export type AnalysisStatus = 'idle' | 'queued' | 'running' | 'done' | 'error';

export interface ExpedienteAnalysis {
  status: AnalysisStatus;
  tramite?: string | null;
  hoja?: string | null;
  applicant?: Record<string, string>;
  coverage?: CoverageItem[];
  /** Store que rellena el PDF (OCR + ediciones manuales) — card 165. */
  form_data?: Record<string, string>;
  /** Campos fijados a mano por el humano (mandan sobre el OCR) — card 165. */
  form_data_manual?: string[];
  updated_at?: string;
  error?: string;
}

/**
 * Normaliza un item de checklist del backend → DocumentoRequisito.
 * El backend de `/checklist` devuelve `{tipo, categoria, descripcion, presente, estado,
 * archivo_id}`; el front consume `{id, codigo, nombre, obligatorio, presentado}`. Si el item ya
 * viene con la forma del front (tiene `nombre`), se respeta tal cual.
 */
export function normalizeChecklistItems(rawList: unknown[]): DocumentoRequisito[] {
  return (rawList ?? []).map((raw) => {
    const it = (raw ?? {}) as Record<string, unknown>;
    if (typeof it.nombre === 'string' && (it.id != null || it.codigo != null)) {
      return it as unknown as DocumentoRequisito;
    }
    const tipo = String(it.tipo ?? it.codigo ?? it.id ?? '');
    const estado = String(it.estado ?? '');
    const categoria = String(it.categoria ?? '');
    return {
      id: String(it.id ?? tipo),
      codigo: tipo,
      nombre: String(it.descripcion ?? it.nombre ?? tipo),
      descripcion: typeof it.descripcion === 'string' ? it.descripcion : undefined,
      obligatorio: categoria === 'obligatorio' || it.obligatorio === true,
      presentado: Boolean(it.presente ?? it.presentado),
      completado_por_ia: estado === 'aprobado' || estado === 'validado',
      archivo_id: (it.archivo_id as string | undefined) ?? undefined,
      categoria: categoria || undefined,
    };
  });
}

export const expedienteApi = {
  /**
   * Crear nuevo expediente
   * POST /api/expedientes/
   */
  async create(data: ExpedienteCreate): Promise<ExpedienteRead> {
    try {
      return await withRetry(async () => {
        const { data: responseData } = await api.post<ExpedienteRead>(
          `${EXPEDIENTES_BASE_PATH}/`,
          data
        );
        return responseData;
      });
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Obtener expediente por ID
   * GET /api/expedientes/{expediente_id}
   */
  async getById(expedienteId: string): Promise<ExpedienteReadWithFiles> {
    try {
      return await withRetry(async () => {
        const { data: responseData } = await api.get<ExpedienteReadWithFiles>(
          `${EXPEDIENTES_BASE_PATH}/${expedienteId}`
        );
        return responseData;
      });
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Lanzar análisis OCR + match (background) — POST /api/expedientes/{id}/analyze-documents
   */
  async startDocumentAnalysis(expedienteId: string): Promise<{ status: string }> {
    const { data } = await api.post<{ status: string }>(
      `${EXPEDIENTES_BASE_PATH}/${expedienteId}/analyze-documents`
    );
    return data;
  },

  /**
   * Estado/resultado del análisis (polling) — GET /api/expedientes/{id}/analyze-documents
   */
  async getDocumentAnalysis(expedienteId: string): Promise<ExpedienteAnalysis> {
    const { data } = await api.get<ExpedienteAnalysis>(
      `${EXPEDIENTES_BASE_PATH}/${expedienteId}/analyze-documents`
    );
    return data;
  },

  /**
   * Aplicar el análisis al cliente — POST /api/expedientes/{id}/analyze-documents/apply
   */
  async applyDocumentAnalysis(
    expedienteId: string
  ): Promise<{ updated_fields: string[]; contact_id: string }> {
    const { data } = await api.post<{ updated_fields: string[]; contact_id: string }>(
      `${EXPEDIENTES_BASE_PATH}/${expedienteId}/analyze-documents/apply`
    );
    return data;
  },

  /**
   * Fijar/editar un campo del formulario a mano — PATCH /api/expedientes/{id}/form-data.
   * Lo manual manda sobre el OCR y rellena el PDF (card 163/165, Stage 2).
   */
  async patchFormData(
    expedienteId: string,
    campo: string,
    valor: string
  ): Promise<{ campo: string; valor: string; form_data: Record<string, string> }> {
    const { data } = await api.patch<{
      campo: string;
      valor: string;
      form_data: Record<string, string>;
    }>(`${EXPEDIENTES_BASE_PATH}/${expedienteId}/form-data`, { campo, valor });
    return data;
  },

  /**
   * Obtener o crear expediente por usuario
   * GET /api/expedientes/user/{user_id}
   */
  async getOrCreateByUser(userId: string): Promise<ExpedienteReadWithFiles> {
    const { data } = await api.get<ExpedienteReadWithFiles>(
      `${EXPEDIENTES_BASE_PATH}/user/${userId}`
    );
    return data;
  },

  /**
   * Actualizar expediente
   * PUT /api/expedientes/{expediente_id}
   */
  async update(
    expedienteId: string,
    data: ExpedienteUpdate
  ): Promise<ExpedienteRead> {
    const { data: response } = await api.put<ExpedienteRead>(
      `${EXPEDIENTES_BASE_PATH}/${expedienteId}`,
      data
    );
    return response;
  },

  /**
   * Eliminar expediente (soft delete por defecto)
   * DELETE /api/expedientes/{expediente_id}?hard_delete=false
   */
  async delete(expedienteId: string, hardDelete = false): Promise<void> {
    await api.delete(`${EXPEDIENTES_BASE_PATH}/${expedienteId}`, {
      params: { hard_delete: hardDelete },
    });
  },

  /**
   * Listar expedientes con filtros
   * GET /api/expedientes/
   */
  async list(filters?: ExpedienteFilters): Promise<ExpedienteListResponse> {
    try {
      return await withRetry(async () => {
        const { data: responseData } = await api.get<ExpedienteListResponse>(
          `${EXPEDIENTES_BASE_PATH}/`,
          { params: filters }
        );
        return responseData;
      });
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Proponer trámite por IA a partir de datos base del expediente
   * POST /api/expedientes/{expediente_id}/proponer-tramite
   * Body: ninguno. Devuelve hasta 3 sugerencias con score y reasoning.
   */
  async proponerTramite(expedienteId: string): Promise<ProponerTramiteResponse> {
    try {
      return await withRetry(async () => {
        const { data: responseData } = await api.post<ProponerTramiteResponse>(
          `${EXPEDIENTES_BASE_PATH}/${expedienteId}/proponer-tramite`
        );
        return responseData;
      });
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Seleccionar formulario oficial
   * POST /api/expedientes/{expediente_id}/seleccionar-formulario
   * Body: { formulario_codigo?: string } O { formulario_oficial_id?: string }, no mezclar.
   * Si solo tienes el código (ej. EX-10), envía formulario_codigo; si tienes el UUID, formulario_oficial_id.
   */
  async seleccionarFormulario(
    expedienteId: string,
    payload: { formulario_codigo?: string; formulario_oficial_id?: string },
    justificacion?: string
  ): Promise<FormularioSeleccionResponse> {
    const body: Record<string, unknown> = { ...payload };
    if (justificacion != null) body.justificacion = justificacion;
    const { data } = await api.post<FormularioSeleccionResponse>(
      `${EXPEDIENTES_BASE_PATH}/${expedienteId}/seleccionar-formulario`,
      body
    );
    return data;
  },

  /**
   * Catálogo de tipos de trámite (búsqueda rápida)
   * GET /api/expedientes/tipos
   */
  async getTipos(): Promise<ExpedienteTiposResponse> {
    const { data } = await api.get<ExpedienteTiposResponse>(
      `${EXPEDIENTES_BASE_PATH}/tipos`
    );
    return data;
  },

  /**
   * Listado detallado de todos los trámites
   * GET /api/expedientes/tipos/detallado
   */
  async getTiposDetallado(): Promise<ExpedienteTiposDetalladoResponse> {
    const { data } = await api.get<ExpedienteTiposDetalladoResponse>(
      `${EXPEDIENTES_BASE_PATH}/tipos/detallado`
    );
    return data;
  },

  /**
   * Ficha legal completa de un trámite
   * GET /api/expedientes/tipos/detallado/{tramite_id}
   */
  async getTramiteDetallado(tramiteId: string): Promise<ExpedienteTipoDetallado> {
    const { data } = await api.get<ExpedienteTipoDetallado>(
      `${EXPEDIENTES_BASE_PATH}/tipos/detallado/${encodeURIComponent(tramiteId)}`
    );
    return data;
  },

  /**
   * Carga maestra por trámite (checklist, requisitos, documentos, flujos, costes, plazos, validaciones, adjuntos, metadata).
   * GET /api/expedientes/tipos/{codigo_tramite}/maestro
   * codigo_tramite: tramite_id (hoja_29), hoja_slug o formulario_codigo (EX-10).
   */
  async getTramiteMaestro(
    codigoTramite: string,
    params?: GetTramiteMaestroParams
  ): Promise<TramiteMaestroResponse> {
    const query: Record<string, string> = {};
    if (params?.version) query.version = params.version;
    if (params?.include) {
      query.include =
        Array.isArray(params.include) ? params.include.join(',') : params.include;
    }
    const { data } = await api.get<TramiteMaestroResponse>(
      `${EXPEDIENTES_BASE_PATH}/tipos/${encodeURIComponent(codigoTramite)}/maestro`,
      { params: Object.keys(query).length ? query : undefined }
    );
    return data;
  },

  /**
   * Obtener catálogo de plantillas/formularios de extranjería (legacy)
   * GET /api/expedientes/plantillas-formularios
   */
  async getPlantillasFormularios(): Promise<PlantillasFormulariosResponse> {
    const { data } = await api.get<PlantillasFormulariosResponse>(
      `${EXPEDIENTES_BASE_PATH}/plantillas-formularios`
    );
    return data;
  },

  /**
   * Validar completitud de documentación
   * GET /api/expedientes/{expediente_id}/completitud
   */
  async getCompletitud(
    expedienteId: string
  ): Promise<CompletitudResponse> {
    const { data } = await api.get<CompletitudResponse>(
      `${EXPEDIENTES_BASE_PATH}/${expedienteId}/completitud`
    );
    return data;
  },

  /**
   * Generar checklist de documentos
   * GET /api/expedientes/{expediente_id}/checklist
   * Normaliza la respuesta por si el backend envía otra clave (documentos, items) o estructura.
   */
  async getChecklist(
    expedienteId: string
  ): Promise<ChecklistResponse> {
    const { data } = await api.get<ChecklistResponse & { documentos?: unknown[]; items?: unknown[] }>(
      `${EXPEDIENTES_BASE_PATH}/${expedienteId}/checklist`
    );
    const raw = data as unknown as Record<string, unknown>;
    const rawList: unknown[] = Array.isArray(data?.documentos_requeridos)
      ? data.documentos_requeridos
      : Array.isArray(raw.documentos)
        ? raw.documentos
        : Array.isArray(raw.items)
          ? raw.items
          : Array.isArray(raw.checklist)
            ? raw.checklist
            : [];
    const items = normalizeChecklistItems(rawList);
    const formulario = (data?.formulario ?? raw.formulario ?? '') as string;
    const progreso = (data?.progreso ?? raw.progreso) as ChecklistResponse['progreso'] | undefined;
    return {
      formulario,
      documentos_requeridos: items as ChecklistResponse['documentos_requeridos'],
      progreso: progreso ?? {
        obligatorios: { total: 0, presentados: 0 },
        opcionales: { total: 0, presentados: 0 },
      },
    };
  },

  /**
   * Obtener requisitos de verificación de la hoja/formulario seleccionado
   * GET /api/expedientes/{expediente_id}/requisitos-verificacion
   */
  async getRequisitosVerificacion(
    expedienteId: string
  ): Promise<RequisitosVerificacionResponse> {
    const { data } = await api.get<RequisitosVerificacionResponse>(
      `${EXPEDIENTES_BASE_PATH}/${expedienteId}/requisitos-verificacion`
    );
    return data;
  },

  /**
   * Obtener payload de formulario relleno
   * GET /api/expedientes/{expediente_id}/formulario-relleno-payload
   */
  async getFormularioRellenoPayload(expedienteId: string): Promise<Record<string, unknown>> {
    const { data } = await api.get<Record<string, unknown>>(
      `${EXPEDIENTES_BASE_PATH}/${expedienteId}/formulario-relleno-payload`
    );
    return data;
  },

  /**
   * Obtener documento de formulario relleno
   * GET /api/expedientes/{expediente_id}/documento-formulario-relleno
   */
  async getDocumentoFormularioRelleno(expedienteId: string): Promise<string | null> {
    const { data } = await api.get<{ url?: string | null } | string>(
      `${EXPEDIENTES_BASE_PATH}/${expedienteId}/documento-formulario-relleno`
    );
    if (typeof data === 'string') return data;
    if (data != null && typeof data === 'object' && 'url' in data) {
      return typeof data.url === 'string' ? data.url : null;
    }
    return null;
  },

  /**
   * Obtener historial de cambios
   * GET /api/expedientes/{expediente_id}/historial
   */
  async getHistorial(
    expedienteId: string,
    skip = 0,
    limit = 50
  ): Promise<ExpedienteHistorialResponse> {
    const { data } = await api.get<ExpedienteHistorialResponse>(
      `${EXPEDIENTES_BASE_PATH}/${expedienteId}/historial`,
      { params: { skip, limit } }
    );
    return data;
  },

  /**
   * Cambiar estado del expediente
   * POST /api/expedientes/{expediente_id}/cambiar-estado
   */
  async cambiarEstado(
    expedienteId: string,
    nuevoStatus: string,
    comentario?: string
  ): Promise<ExpedienteRead> {
    const { data } = await api.post<ExpedienteRead>(
      `${EXPEDIENTES_BASE_PATH}/${expedienteId}/cambiar-estado`,
      {
        nuevo_status: nuevoStatus,
        comentario,
      }
    );
    return data;
  },

  /**
   * Obtener estadísticas del expediente
   * GET /api/expedientes/{expediente_id}/estadisticas
   */
  async getEstadisticas(
    expedienteId: string
  ): Promise<ExpedienteEstadisticas> {
    const { data } = await api.get<ExpedienteEstadisticas>(
      `${EXPEDIENTES_BASE_PATH}/${expedienteId}/estadisticas`
    );
    return data;
  },

  /**
   * Búsqueda avanzada
   * GET /api/expedientes/buscar
   */
  async buscar(
    query: string,
    filters?: {
      formulario?: string;
      status?: string;
      page?: number;
      limit?: number;
    }
  ): Promise<ExpedienteSearchResponse> {
    const { data } = await api.get<ExpedienteRead[] | ExpedienteSearchResponse | null>(
      `${EXPEDIENTES_BASE_PATH}/buscar`,
      {
        params: {
          q: query,
          ...filters,
        },
      }
    );
    // El endpoint devuelve List[ExpedienteRead] (array pelado), no el objeto
    // paginado que espera el frontend. Normalizamos a ExpedienteSearchResponse
    // para que la lista pueda leer .items sin romper.
    if (Array.isArray(data)) {
      return { items: data, total: data.length, page: 1, limit: data.length };
    }
    if (!data) {
      return { items: [], total: 0, page: 1, limit: 0 };
    }
    return data;
  },

  /**
   * Subir archivo al expediente
   * POST /api/expedientes/{expediente_id}/archivos
   */
  async uploadFile(
    expedienteId: string,
    file: File,
    metadata?: {
      nombre?: string;
      tipo?: string;
    }
  ): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    if (metadata?.nombre) {
      formData.append('nombre', metadata.nombre);
    }
    if (metadata?.tipo) {
      formData.append('tipo', metadata.tipo);
    }

    const { data } = await api.post(
      `${EXPEDIENTES_BASE_PATH}/${expedienteId}/archivos`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return data;
  },

  /**
   * Cambiar estado de archivo
   * PATCH /api/expedientes/{expediente_id}/archivos/{archivo_id}
   */
  async updateFileStatus(
    expedienteId: string,
    archivoId: string,
    estado: 'pendiente' | 'aprobado' | 'rechazado',
    validationNotes?: string
  ): Promise<any> {
    const { data } = await api.patch(
      `${EXPEDIENTES_BASE_PATH}/${expedienteId}/archivos/${archivoId}`,
      {
        estado,
        validation_notes: validationNotes,
      }
    );
    return data;
  },

  /**
   * Proceder al trámite: valida "listo para presentar" + genera el paquete output
   * (formulario EX-NN + anexos) y deja el expediente en ready_to_submit (card 87).
   * POST /api/expedientes/{expediente_id}/proceder
   * Lanza 409 con { blockers: string[] } si aún no está listo.
   */
  async proceder(expedienteId: string): Promise<{
    estado: string;
    estado_final: string;
    listo: boolean;
    blockers: string[];
    download_url?: string | null;
  }> {
    const { data } = await api.post(
      `${EXPEDIENTES_BASE_PATH}/${expedienteId}/proceder`
    );
    return data;
  },

  /**
   * Descarga el ZIP del paquete output (tras proceder / POST output-package).
   * GET /api/expedientes/{expediente_id}/output-package/download
   */
  async downloadOutputPackage(
    expedienteId: string
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await api.get(
      `${EXPEDIENTES_BASE_PATH}/${expedienteId}/output-package/download`,
      { responseType: 'blob' }
    );
    const disposition = String(response.headers?.['content-disposition'] ?? '');
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    const filename = match ? decodeURIComponent(match[1]) : 'expediente.zip';
    return { blob: response.data as Blob, filename };
  },
};

